import type { InventoryItem } from '../types/inventory';
import {
  DEFAULT_LABEL_BARCODE_VARIANT,
  LABEL_BARCODE_HEIGHT_MM,
  LABEL_BARCODE_TOP_MM,
  LABEL_BARCODE_VARIANTS,
  LABEL_META_FONT_PT,
  LABEL_META_HEIGHT_MM,
  LABEL_PANEL_INSET_MM,
  LABEL_TOP_PAD_MM,
  labelBarcodePng,
  labelMetaWidthMm,
  type LabelBarcodeVariant,
} from './inventoryLabelBarcode';
import { buildInventoryLabelsEpl } from './inventoryLabelEpl';
import {
  LABEL_BARCODE_LEFT_MM,
  LABEL_BARCODE_WIDTH_MM,
  LABEL_EMPTY_MM,
  LABEL_FOLD_MM,
  LABEL_FONT_FAMILY,
  LABEL_TALLA_FONT_PT,
  LABEL_PROVIDER_FONT_PT,
  LABEL_INFO_FONT_PT,
  LABEL_INFO_LEFT_MM,
  LABEL_INFO_PAD_X_MM,
  LABEL_INFO_PROVIDER_GAP_MM,
  LABEL_INFO_WIDTH_MM,
  LABEL_DOTS_PER_MM,
  LABEL_PRINT_HEIGHT_MM,
  LABEL_PRINT_WIDTH_MM,
  LABEL_TEXT_LETTER_SPACING_MM,
} from './inventoryLabelGeometry';
import { printRawEplSilent, type LabelPrintResult } from './labelPrinter';
import { escapeHtml, openPrintWindow, PRINT_POPUP_BLOCKED_MESSAGE } from './printWindow';

export {
  LABEL_PRINT_WIDTH_MM,
  LABEL_PRINT_HEIGHT_MM,
  LABEL_PRINT_GAP_MM,
  LABEL_FOLD_MM,
  LABEL_EMPTY_MM,
  LABEL_INFO_LEFT_MM,
  LABEL_BARCODE_LEFT_MM,
  LABEL_INFO_WIDTH_MM,
  LABEL_BARCODE_WIDTH_MM,
} from './inventoryLabelGeometry';

export interface InventoryLabelPrintRow {
  productCodeUnique: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
  diameterSize?: string | null;
  workshopCode?: string | null;
  /** Bar width; only the test sheet sets it. */
  barcodeVariant?: LabelBarcodeVariant;
}

function optionalLabelText(value: string | null | undefined): string {
  return (value ?? '').trim();
}

/** Blank optional label fields are omitted from the layout, not printed as empty boxes. */
function optionalLabelTextOrNull(value: string | null | undefined): string | null {
  const text = optionalLabelText(value);
  return text === '' ? null : text;
}

function formatPriceBs(price: number): string {
  const n = Number(price);
  const s = n.toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `Bs. ${s}`;
}

function formatWeight(weightGrams: number | null): string {
  if (weightGrams == null || Number.isNaN(Number(weightGrams))) return '';
  return Number(weightGrams).toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function pngImg(dataUrl: string, className: string, style = ''): string {
  if (dataUrl === '') return '';
  const styleAttr = style === '' ? '' : ` style="${style}"`;
  return `<img class="${className}" src="${dataUrl}" alt=""${styleAttr} />`;
}

/** Prefer silent EPL; fall back to system print dialog (Windows Zebra driver). */
export async function printInventoryLabels(
  rows: InventoryLabelPrintRow[],
): Promise<LabelPrintResult> {
  if (rows.length === 0) {
    return { ok: false, message: 'No hay etiquetas seleccionadas.' };
  }
  const epl = buildInventoryLabelsEpl(rows);
  const silent = await printRawEplSilent(epl);
  if (silent.ok) return silent;

  openInventoryLabelsPrintWindow(rows, true);
  return { ok: true, channel: 'system-dialog' };
}

/** Page setup shared by every sheet sent to the label printer: one 88 × 10 mm page per label. */
const LABEL_SHEET_BASE_CSS = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    @page {
      size: ${LABEL_PRINT_WIDTH_MM}mm ${LABEL_PRINT_HEIGHT_MM}mm;
      margin: 0;
    }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      width: ${LABEL_PRINT_WIDTH_MM}mm;
      font-family: ${LABEL_FONT_FAMILY};
      font-weight: bold;
      background: #fff;
      color: #000;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .label-page {
      position: relative;
      width: ${LABEL_PRINT_WIDTH_MM}mm;
      height: ${LABEL_PRINT_HEIGHT_MM}mm;
      overflow: hidden;
      page-break-after: always;
      break-after: page;
    }
    .label-page:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    @media screen {
      body {
        width: ${LABEL_PRINT_WIDTH_MM}mm;
        margin: 8px auto;
        background: #e5e7eb;
      }
      .label-page {
        margin-bottom: 8px;
        background: #fff;
        outline: 1px solid #94a3b8;
      }
      /* Physical fold on the adhesive neck — stays put even though content moved. */
      .label-page::before {
        content: '';
        position: absolute;
        left: ${LABEL_FOLD_MM}mm;
        top: 0; bottom: 0;
        border-left: 1px dotted #94a3b8;
        z-index: 2;
        pointer-events: none;
      }
      /* End of the adhesive neck: nothing may print to the left of this line. */
      .label-page::after {
        content: '';
        position: absolute;
        left: ${LABEL_EMPTY_MM}mm;
        top: 0; bottom: 0;
        border-left: 1px dashed #64748b;
        z-index: 2;
        pointer-events: none;
      }
    }
`;

/** Opens a print window with one `.label-page` per label, through the shop's driver dialog. */
function openLabelSheet(bodyHtml: string, css: string, autoPrint: boolean): void {
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>&nbsp;</title>
  <style>${LABEL_SHEET_BASE_CSS}${css}</style>
</head>
<body>${bodyHtml}</body>
</html>`;

  const printWindow = openPrintWindow({
    html,
    size: 'labels',
    autoPrint,
    autoPrintDelayMs: 500,
    windowName: 'printLabels',
  });

  if (!printWindow) {
    window.alert(PRINT_POPUP_BLOCKED_MESSAGE);
  }
}

/**
 * Chrome → ZDesigner label:
 * - all text as bold Times New Roman CSS text: the driver rasters the font outlines at
 *   203 dpi, which prints cleaner than text we raster into a PNG ourselves
 * - barcode PNG
 */
export function openInventoryLabelsPrintWindow(
  rows: InventoryLabelPrintRow[],
  autoPrint = false,
): void {
  if (rows.length === 0) return;

  const labelsHtml = rows
    .map((row) => {
      const bars = labelBarcodePng(
        row.productCodeUnique,
        row.barcodeVariant ?? DEFAULT_LABEL_BARCODE_VARIANT,
      );
      const barsHtml =
        bars == null ? '' : pngImg(bars.dataUrl, 'bar-img', `width: ${bars.widthMm}mm`);
      const talla = optionalLabelText(row.diameterSize);
      const workshopCode = optionalLabelText(row.workshopCode);
      const weight = formatWeight(row.weightGrams);
      const metaWidthMm = labelMetaWidthMm(bars?.widthMm ?? 0);

      return `<div class="label-page">
  <div class="panel panel-info">
    <div class="mat">${escapeHtml(row.materialLabel)}</div>
    <div class="price-line">
      <div class="price">${escapeHtml(formatPriceBs(row.salePrice))}</div>
      <span class="talla">${escapeHtml(talla)}</span>
    </div>
    <div class="workshop-line">${escapeHtml(workshopCode)}</div>
  </div>
  <div class="panel panel-barcode">
    <div class="code-block" style="width: ${metaWidthMm}mm">
      <div class="meta">
        <span>${escapeHtml(row.productCodeUnique)}</span>
        <span>${escapeHtml(weight)}</span>
      </div>
      <div class="bar-slot">${barsHtml}</div>
    </div>
  </div>
</div>`;
    })
    .join('');

  openLabelSheet(
    labelsHtml,
    `
    .panel-barcode {
      position: absolute;
      left: ${LABEL_BARCODE_LEFT_MM}mm;
      top: 0;
      bottom: 0;
      width: ${LABEL_BARCODE_WIDTH_MM}mm;
      overflow: hidden;
    }
    /* Width comes inline per label: peso ends on the last bar, never past the safe edge. */
    .code-block {
      position: absolute;
      left: ${LABEL_PANEL_INSET_MM}mm;
      top: ${LABEL_TOP_PAD_MM}mm;
    }
    /* line-height = row height keeps the bars in place whatever the text. */
    .meta {
      display: flex;
      justify-content: space-between;
      gap: 0.5mm;
      height: ${LABEL_META_HEIGHT_MM}mm;
      font-size: ${LABEL_META_FONT_PT}pt;
      line-height: ${LABEL_META_HEIGHT_MM}mm;
      letter-spacing: ${LABEL_TEXT_LETTER_SPACING_MM}mm;
      white-space: nowrap;
    }
    /* A line longer than the block pushes peso right (the panel clips at 88 mm) instead of overlapping. */
    .meta > span { flex: 0 0 auto; }
    .bar-slot {
      margin-top: ${LABEL_BARCODE_TOP_MM - LABEL_TOP_PAD_MM - LABEL_META_HEIGHT_MM}mm;
      height: ${LABEL_BARCODE_HEIGHT_MM}mm;
    }
    /* Width comes inline per label; both sizes are set so the PNG is never letterboxed. */
    .bar-img {
      display: block;
      height: ${LABEL_BARCODE_HEIGHT_MM}mm;
      image-rendering: pixelated;
      image-rendering: crisp-edges;
    }
    .panel-info {
      position: absolute;
      left: ${LABEL_INFO_LEFT_MM}mm;
      top: 0;
      bottom: 0;
      width: ${LABEL_INFO_WIDTH_MM}mm;
      padding: 0 ${LABEL_INFO_PAD_X_MM}mm;
      display: flex;
      flex-direction: column;
      justify-content: center;
      overflow: hidden;
      font-size: ${LABEL_INFO_FONT_PT}pt;
      line-height: 1;
    }
    .mat {
      font-style: italic;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    /* Talla shares the price baseline, one point smaller. */
    .price-line {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 0.5mm;
    }
    .price {
      flex: 0 1 auto;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
    }
    .talla {
      flex: 0 0 auto;
      font-size: ${LABEL_TALLA_FONT_PT}pt;
      white-space: nowrap;
    }
    /* Always reserved, so material and price sit in the same place with or without it. */
    .workshop-line {
      font-size: ${LABEL_PROVIDER_FONT_PT}pt;
      margin-top: ${LABEL_INFO_PROVIDER_GAP_MM}mm;
      height: 1em;
      white-space: nowrap;
      overflow: hidden;
    }
`,
    autoPrint,
  );
}

/** Una etiqueta desde fila de almacén/sucursal. */
export async function printInventoryUnitLabel(item: InventoryItem): Promise<LabelPrintResult> {
  return printInventoryLabels([
    {
      productCodeUnique: String(item.id),
      materialLabel: optionalLabelTextOrNull(item.material) ?? '—',
      salePrice: item.price ?? 0,
      weightGrams: item.weightGrams ?? null,
      diameterSize: optionalLabelTextOrNull(item.diameterSize),
      workshopCode: optionalLabelTextOrNull(item.workshopCode),
    },
  ]);
}

/** Worst cases on the roll: 4- and 5-digit codes, the usual provider code, empty fields. */
const LABEL_TEST_CASES: Array<Omit<InventoryLabelPrintRow, 'materialLabel' | 'barcodeVariant'>> = [
  {
    productCodeUnique: '9929/5',
    salePrice: 350,
    weightGrams: 3.25,
    diameterSize: '18',
    workshopCode: 'CP-AN-023',
  },
  {
    productCodeUnique: '10000/1',
    salePrice: 12350,
    weightGrams: 12.35,
    diameterSize: 'XL',
    workshopCode: 'CP-AN-023',
  },
  {
    productCodeUnique: '10000/12',
    salePrice: 1250,
    weightGrams: 3.25,
    diameterSize: 'M',
    workshopCode: 'CP-AN-023',
  },
  { productCodeUnique: '10000/12', salePrice: 350, weightGrams: 3.25 },
  { productCodeUnique: '10000/12', salePrice: 350, weightGrams: null },
];

/** The material panel names the bar width, so the shop can tell which variant scanned. */
const LABEL_TEST_VARIANT_NAMES: Record<LabelBarcodeVariant, string> = {
  standard: 'PRUEBA A',
  thin: 'PRUEBA B',
};

/**
 * Test sheet: every case once per bar width, through the same dialog the shop uses.
 * Not wired to any button: the shop prints with the standard width. Kept for a later
 * scanner test with the thin width; remove both after the next release window if unused.
 */
export function openInventoryLabelTestSheet(): void {
  openInventoryLabelsPrintWindow(
    LABEL_BARCODE_VARIANTS.flatMap((variant) =>
      LABEL_TEST_CASES.map((testCase) => ({
        ...testCase,
        materialLabel: LABEL_TEST_VARIANT_NAMES[variant],
        barcodeVariant: variant,
      })),
    ),
    true,
  );
}

/** Ruler tick spacing; every fifth tick is long and numbered. */
const CALIBRATION_TICK_STEP_MM = 1;
const CALIBRATION_MAJOR_EVERY = 5;
/** Two printer dots, so a tick survives a slightly light print. */
const CALIBRATION_TICK_WIDTH_MM = 2 / LABEL_DOTS_PER_MM;

/**
 * Calibration sheet: a mm ruler over the printable tail (45–88 mm), through the same
 * driver dialog as real labels. Full-height lines mark where the layout expects the
 * die-cut pieces (63 mm end of Plata/Bs, 68 mm start of the barcode). On the printed
 * strip, the distance between the 68 line and the real start of the barcode piece is
 * the printer's horizontal shift; the last tick that prints shows where the right edge
 * cuts. Ticks at the top and bottom edges show a vertical shift the same way.
 */
export function openInventoryLabelCalibrationSheet(): void {
  const ticks: string[] = [];
  // Starts 1 mm past the neck: a tick centred on 44 mm would put a dot on the adhesive.
  const firstTickMm = LABEL_EMPTY_MM + CALIBRATION_TICK_STEP_MM;
  for (let mm = firstTickMm; mm < LABEL_PRINT_WIDTH_MM; mm += CALIBRATION_TICK_STEP_MM) {
    const major = mm % CALIBRATION_MAJOR_EVERY === 0;
    const left = mm - CALIBRATION_TICK_WIDTH_MM / 2;
    const size = major ? 'cal-major' : 'cal-minor';
    ticks.push(`<div class="cal-tick cal-top ${size}" style="left: ${left}mm"></div>`);
    ticks.push(`<div class="cal-tick cal-bottom ${size}" style="left: ${left}mm"></div>`);
    if (major) {
      ticks.push(`<div class="cal-num" style="left: ${mm}mm">${mm}</div>`);
    }
  }
  const panelEdges = [LABEL_INFO_LEFT_MM + LABEL_INFO_WIDTH_MM, LABEL_BARCODE_LEFT_MM].map(
    (mm) => `<div class="cal-line" style="left: ${mm - CALIBRATION_TICK_WIDTH_MM / 4}mm"></div>`,
  );
  // The last two dots of the strip: if this bar is missing, the right edge is cut.
  const label = `<div class="label-page">${ticks.join('')}${panelEdges.join('')}<div class="cal-edge"></div></div>`;

  openLabelSheet(
    label + label,
    `
    .cal-tick, .cal-line, .cal-edge { position: absolute; background: #000; }
    .cal-tick { width: ${CALIBRATION_TICK_WIDTH_MM}mm; }
    .cal-top { top: 0; }
    .cal-bottom { bottom: 0; }
    .cal-minor { height: 1mm; }
    .cal-major { height: 2.5mm; }
    .cal-num {
      position: absolute;
      top: 3.6mm;
      font-size: ${LABEL_META_FONT_PT}pt;
      line-height: 1;
      transform: translateX(-50%);
    }
    .cal-line { top: 0; bottom: 0; width: ${CALIBRATION_TICK_WIDTH_MM / 2}mm; }
    .cal-edge { top: 0; bottom: 0; right: 0; width: ${CALIBRATION_TICK_WIDTH_MM}mm; }
`,
    true,
  );
}
